import React from 'react';
import PropTypes from 'prop-types';
import { Modal as BootstrapModal } from 'react-bootstrap';
import styled from '@emotion/styled';
import { theme } from '../../constants/theme';

const StyledCustomModal = styled(BootstrapModal)`
  .modal-content {
    border-radius: ${theme.borderRadius.md};
    border: none;
    box-shadow: ${theme.shadows.xl};
  }

  .modal-header {
    border-bottom: 1px solid ${theme.colors.gray[200]};
    padding: ${theme.spacing.lg};

    .modal-title {
      font-family: ${theme.typography.fontFamily};
      font-size: ${theme.typography.fontSize.lg};
      font-weight: ${theme.typography.fontWeight.semibold};
      color: ${theme.colors.text.primary};
    }

    .btn-close {
      opacity: 0.5;
      
      &:hover {
        opacity: 1;
      }
    }
  }

  .modal-body {
    padding: ${theme.spacing.lg};
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.fontSize.base};
    color: ${theme.colors.text.primary};
  }

  .modal-footer {
    border-top: 1px solid ${theme.colors.gray[200]};
    padding: ${theme.spacing.lg};
    display: flex;
    justify-content: flex-end;
    gap: ${theme.spacing.sm};
  }
`;

/**
 * CustomModal Component
 * 
 * A customizable modal dialog component based on React Bootstrap Modal.
 * 
 * @component
 * @example
 * ```jsx
 * <CustomModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   title="Confirm Action"
 *   footer={
 *     <>
 *       <CustomButton variant="text" onClick={onCancel}>Cancel</CustomButton>
 *       <CustomButton variant="primary" onClick={onConfirm}>Confirm</CustomButton>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to proceed?</p>
 * </CustomModal>
 * ```
 */
const CustomModal = ({
  isOpen = false,
  onClose,
  title = '',
  children,
  footer = null,
  size = 'md',
  centered = true,
  closeButton = true,
  backdrop = true,
  keyboard = true,
  className = '',
}) => {
  return (
    <StyledCustomModal
      show={isOpen}
      onHide={onClose}
      size={size}
      centered={centered}
      backdrop={backdrop}
      keyboard={keyboard}
      className={className}
    >
      {title && (
        <BootstrapModal.Header closeButton={closeButton}>
          <BootstrapModal.Title>{title}</BootstrapModal.Title>
        </BootstrapModal.Header>
      )}

      <BootstrapModal.Body>{children}</BootstrapModal.Body>

      {footer && <BootstrapModal.Footer>{footer}</BootstrapModal.Footer>}
    </StyledCustomModal>
  );
};

CustomModal.propTypes = {
  /** Modal open state */
  isOpen: PropTypes.bool.isRequired,
  
  /** Close handler */
  onClose: PropTypes.func.isRequired,
  
  /** Modal title */
  title: PropTypes.string,
  
  /** Modal content */
  children: PropTypes.node.isRequired,
  
  /** Modal footer content (buttons, actions) */
  footer: PropTypes.node,
  
  /** Modal size */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  
  /** Center modal vertically */
  centered: PropTypes.bool,
  
  /** Show close button in header */
  closeButton: PropTypes.bool,
  
  /** Backdrop behavior ('static', true, false) */
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['static'])]),
  
  /** Close on Escape key */
  keyboard: PropTypes.bool,
  
  /** Additional CSS classes */
  className: PropTypes.string,
};

export default CustomModal;
